import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  getAllJSON,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";

// Generyczny CRUD słownika (personel): lista + utwórz + przełącz `active`.
// Jedna instancja = jeden zasób (Kategorie / Podkategorie / Cennik), konfigurowany przez values.
// Formularz zbiera inputy po atrybucie `name`; select opcji (kategoria/podkategoria) zasilany z optionsUrl.
// 0 logiki pieniędzy w JS — kwoty to netto wpisywane przez personel; VAT dolicza backend przy zamówieniu.
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "formError",
    "optionSelect",
    "form",
    "submitBtn",
    "cancel",
    "levelSelect", // cennik: wybór poziomu (global/kategoria/podkategoria)
    "levelField", // cennik: pole widoczne tylko dla danego poziomu (data-level)
    "globalFlag", // cennik: ukryty checkbox name=default_global sterowany poziomem
    "categorySelect", // cennik: select kategorii (poziom „kategoria")
  ];
  static values = {
    url: String, // /api/<resource>/
    templateId: String, // id <template> wiersza
    optionsUrl: String, // opcjonalnie: źródło opcji selecta (np. /api/subcategories/)
    optionsLabel: String, // pole etykiety opcji (np. "name")
    categoriesUrl: String, // cennik: źródło opcji kategorii (/api/categories/)
    categoriesLabel: String, // cennik: pole etykiety kategorii (np. "name")
    canWrite: Boolean, // czy pokazywać akcje zapisu (przy 403 = false)
  };

  connect() {
    this._items = [];
    this._editId = null;
    this._optionsById = new Map(); // id podkategorii → nazwa (etykieta zamiast id)
    this._categoriesById = new Map(); // id kategorii → nazwa
    // Zapamiętaj oryginalną etykietę „Dodaj…", by wrócić do niej po wyjściu z edycji.
    this._createLabel = this.hasSubmitBtnTarget
      ? this.submitBtnTarget.textContent
      : "Dodaj";
    // Opcje muszą być gotowe PRZED renderem listy — wiersze cennika składają nazwy z mapy.
    this.#init();
  }

  async #init() {
    await this.#loadOptions();
    await this.#loadCategories();
    if (this.hasLevelSelectTarget) this.changeLevel();
    await this.load();
  }

  async load() {
    let data;
    try {
      data = await getJSON(this.urlValue);
    } catch {
      this.#status("Nie udało się załadować danych. Odśwież stronę.");
      return;
    }
    const items = listResults(data);
    this._items = items;
    this.rowsTarget.textContent = "";
    if (!items.length) {
      this.#status("Brak pozycji.");
      return;
    }
    this.#status(null);
    const template = document.getElementById(this.templateIdValue);
    for (const item of items) {
      const row = template.content.firstElementChild.cloneNode(true);
      for (const cell of row.querySelectorAll("[data-field]")) {
        const value = item[cell.dataset.field];
        cell.textContent =
          value === true ? "tak" : value === false ? "nie" : (value ?? "—");
      }
      // Cennik: złożona etykieta (poziom + nazwa) zamiast surowego id.
      const labelCell = row.querySelector("[data-crud-label]");
      if (labelCell) labelCell.textContent = this.#composeLabel(item);
      const toggle = row.querySelector("[data-crud-toggle]");
      if (toggle) {
        if (!this.canWriteValue) {
          toggle.hidden = true;
        } else {
          toggle.hidden = false;
          toggle.setAttribute("data-action", "crud#toggle");
          toggle.setAttribute("data-crud-id-param", item.id);
          toggle.setAttribute("data-crud-active-param", String(item.active));
          toggle.textContent = item.active ? "Dezaktywuj" : "Aktywuj";
        }
      }
      const edit = row.querySelector("[data-crud-edit]");
      if (edit) {
        if (!this.canWriteValue) {
          edit.hidden = true;
        } else {
          edit.hidden = false;
          edit.type = "button";
          edit.setAttribute("data-action", "crud#edit");
          edit.setAttribute("data-crud-id-param", item.id);
          edit.textContent = "Edytuj";
        }
      }
      const del = row.querySelector("[data-crud-delete]");
      if (del) {
        if (!this.canWriteValue) {
          del.hidden = true;
        } else {
          del.hidden = false;
          del.type = "button";
          del.setAttribute("data-action", "crud#delete");
          del.setAttribute("data-crud-id-param", item.id);
          del.textContent = "Usuń";
        }
      }
      this.rowsTarget.appendChild(row);
    }
  }

  // Submit formularza: POST (dodawanie) albo PATCH `${url}${id}/` (tryb edycji).
  async submit(event) {
    event.preventDefault();
    const form = this.hasFormTarget
      ? this.formTarget
      : (event.currentTarget.closest("form") ?? event.currentTarget);
    const payload = this.#collect(form);
    const editing = this._editId != null;
    const res = editing
      ? await sendJSON("PATCH", `${this.urlValue}${this._editId}/`, payload)
      : await sendJSON("POST", this.urlValue, payload);
    if (!res.ok) {
      this.#formError(
        this.#fieldErrors(res.data) ||
          errorDetail(res.data, "Nie udało się zapisać."),
      );
      return;
    }
    this.#formError(null);
    this.#exitEdit();
    form.reset();
    await this.load();
  }

  // Wejście w tryb edycji: wypełnia formularz wartościami itemu i przełącza UI.
  edit(event) {
    const id = Number(event.params.id);
    const item = (this._items ?? []).find((i) => i.id === id);
    if (!item || !this.hasFormTarget) return;
    for (const input of this.formTarget.querySelectorAll("[name]")) {
      const value = item[input.name];
      if (input.type === "checkbox") {
        input.checked = value === true;
      } else {
        input.value = value == null ? "" : value;
      }
    }
    this._editId = id;
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.textContent = "Zapisz zmiany";
    if (this.hasCancelTarget) this.cancelTarget.hidden = false;
    this.#formError(null);
    // Cennik: ustaw poziom formularza na podstawie itemu i pokaż właściwe pole.
    if (this.hasLevelSelectTarget) {
      const level = this.#itemLevel(item);
      this.levelSelectTarget.value = level;
      this.changeLevel();
      // changeLevel czyści nieaktywne pola — przywróć wartość aktywnego poziomu.
      if (level === "subcategory" && this.hasOptionSelectTarget && item.subcategory != null) {
        this.optionSelectTarget.value = item.subcategory;
      }
      if (level === "category" && this.hasCategorySelectTarget && item.category != null) {
        this.categorySelectTarget.value = item.category;
      }
    }
  }

  // „Anuluj" — wyjście z edycji + reset formularza (bez zapisu).
  cancelEdit() {
    this.#exitEdit();
    if (this.hasFormTarget) this.formTarget.reset();
    if (this.hasLevelSelectTarget) this.changeLevel();
  }

  // Cennik: zmiana poziomu (global/kategoria/podkategoria) — pokazuje właściwe
  // pole, czyści nieaktywne i ustawia ukrytą flagę default_global. Dokładnie
  // jeden poziom trafia do payloadu (backend dodatkowo waliduje).
  changeLevel() {
    if (!this.hasLevelSelectTarget) return;
    const level = this.levelSelectTarget.value;
    for (const field of this.levelFieldTargets) {
      const active = field.dataset.level === level;
      field.hidden = !active;
      // Wyczyść pola nieaktywnego poziomu, by nie trafiły do payloadu (zostaną null).
      if (!active) {
        for (const input of field.querySelectorAll("[name]")) input.value = "";
      }
    }
    if (this.hasGlobalFlagTarget) this.globalFlagTarget.checked = level === "global";
  }

  async toggle(event) {
    const active =
      event.params.active === "true" || event.params.active === true;
    const res = await sendJSON("PATCH", `${this.urlValue}${event.params.id}/`, {
      active: !active,
    });
    if (!res.ok) {
      this.#status(errorDetail(res.data, "Nie udało się zmienić statusu."));
      return;
    }
    await this.load();
  }

  // Twardy DELETE (zasoby bez `active`, np. Tag — usuwanie zamiast dezaktywacji).
  // Backend odpowiada 409 {detail} gdy zasób jest w użyciu (np. tag przypisany do leada,
  // LeadListing.tag ma on_delete=PROTECT) — pokazujemy to pod formularzem, nie w statusie,
  // żeby nie znikało przy kolejnym renderze listy.
  async delete(event) {
    const id = Number(event.params.id);
    const item = (this._items ?? []).find((i) => i.id === id);
    const label = item?.name ?? `#${id}`;
    if (!window.confirm(`Usunąć „${label}”? Tej operacji nie można cofnąć.`)) {
      return;
    }
    const res = await sendJSON("DELETE", `${this.urlValue}${id}/`);
    if (!res.ok) {
      this.#formError(errorDetail(res.data, "Nie udało się usunąć."));
      return;
    }
    this.#formError(null);
    await this.load();
  }

  // --- prywatne ---

  #exitEdit() {
    this._editId = null;
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.textContent = this._createLabel;
    }
    if (this.hasCancelTarget) this.cancelTarget.hidden = true;
  }

  #collect(form) {
    const payload = {};
    for (const input of form.querySelectorAll("[name]")) {
      const key = input.name;
      if (input.type === "checkbox") {
        payload[key] = input.checked;
      } else if (input.value === "") {
        payload[key] = null; // pusty select/pole = null (np. subcategory przy default_global)
      } else if (
        input.dataset.number !== undefined ||
        input.type === "number"
      ) {
        payload[key] =
          input.type === "number" ? input.value : Number(input.value);
      } else {
        payload[key] = input.value;
      }
    }
    return payload;
  }

  // Zamień słownik błędów DRF ({pole: [komunikat]}) na jedną czytelną linię.
  #fieldErrors(data) {
    if (!data || typeof data !== "object" || data.detail) return null;
    const parts = [];
    for (const [field, messages] of Object.entries(data)) {
      const text = Array.isArray(messages)
        ? messages.join(" ")
        : String(messages);
      parts.push(`${field}: ${text}`);
    }
    return parts.length ? parts.join(" · ") : null;
  }

  async #loadOptions() {
    if (!this.hasOptionSelectTarget || !this.optionsUrlValue) return;
    let options;
    try {
      options = await getAllJSON(this.optionsUrlValue);
    } catch {
      return;
    }
    const label = this.optionsLabelValue || "name";
    for (const option of options) {
      this._optionsById.set(option.id, option[label]);
      const el = document.createElement("option");
      el.value = option.id;
      el.textContent = option[label];
      this.optionSelectTarget.appendChild(el);
    }
  }

  async #loadCategories() {
    if (!this.hasCategorySelectTarget || !this.categoriesUrlValue) return;
    let options;
    try {
      options = await getAllJSON(this.categoriesUrlValue);
    } catch {
      return;
    }
    const label = this.categoriesLabelValue || "name";
    for (const option of options) {
      this._categoriesById.set(option.id, option[label]);
      const el = document.createElement("option");
      el.value = option.id;
      el.textContent = option[label];
      this.categorySelectTarget.appendChild(el);
    }
  }

  // Etykieta reguły cennika: globalna → „Globalny default"; kategoria →
  // „Kategoria: {nazwa}"; podkategoria → sama nazwa. Nazwy bierzemy z pól
  // *_name (jeśli backend je zwróci) lub z mapy opcji (bez zmian backendu).
  #composeLabel(item) {
    if (item.default_global) return "Globalny default";
    if (item.category_name) return `Kategoria: ${item.category_name}`;
    if (item.subcategory_name) return item.subcategory_name;
    if (item.category != null) {
      return `Kategoria: ${this._categoriesById.get(item.category) ?? `#${item.category}`}`;
    }
    if (item.subcategory != null) {
      return this._optionsById.get(item.subcategory) ?? `#${item.subcategory}`;
    }
    return "—";
  }

  // Poziom reguły wyprowadzony z itemu (na potrzeby edycji formularza).
  #itemLevel(item) {
    if (item.default_global) return "global";
    if (item.category != null && item.subcategory == null) return "category";
    return "subcategory";
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return;
    this.formErrorTarget.hidden = message === null;
    if (message !== null) this.formErrorTarget.textContent = message;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
